import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { Navbar } from "@/components/Navbar";
import { useAuth } from "@/context/AuthContext";
import api, { formatApiErrorDetail } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { KeyRound } from "lucide-react";

const landing = (role) => (role === "admin" ? "/admin" : role === "provider" ? "/provider" : "/dashboard");

export default function ChangePassword() {
  const { user, updateUser } = useAuth();
  const navigate = useNavigate();
  const [form, setForm] = useState({ current_password: "", new_password: "", confirm: "" });
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);
  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });
  const forced = !!user?.must_change_password;

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    if (form.new_password.length < 6) { setError("New password must be at least 6 characters."); return; }
    if (form.new_password !== form.confirm) { setError("New passwords do not match."); return; }
    setBusy(true);
    try {
      await api.post("/auth/change-password", { current_password: form.current_password, new_password: form.new_password });
      updateUser({ must_change_password: false });
      toast.success("Password updated");
      navigate(landing(user?.role));
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally { setBusy(false); }
  };

  return (
    <div className="min-h-screen">
      <Navbar />
      <div className="max-w-md mx-auto px-6 py-16">
        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="rounded-3xl border border-black/5 bg-white p-8 shadow-[0_8px_32px_rgba(0,0,0,0.06)]">
          <div className="grid h-12 w-12 place-items-center rounded-xl bg-emerald-100 text-emerald-700"><KeyRound className="h-6 w-6" /></div>
          <h1 className="mt-4 font-serif text-3xl text-emerald-950">{forced ? "Set your new password" : "Change password"}</h1>
          <p className="mt-2 text-stone-500 text-sm">
            {forced
              ? "Welcome to PlantFlow! For your security, please replace your temporary password before continuing."
              : "Enter your current password and choose a new one."}
          </p>
          <form onSubmit={submit} className="mt-6 space-y-4">
            <div>
              <Label className="text-xs uppercase tracking-[0.2em] text-stone-500">{forced ? "Temporary password" : "Current password"}</Label>
              <Input data-testid="cp-current" type="password" value={form.current_password} onChange={set("current_password")} required className="mt-1.5 rounded-xl" placeholder="••••••••" />
            </div>
            <div>
              <Label className="text-xs uppercase tracking-[0.2em] text-stone-500">New password</Label>
              <Input data-testid="cp-new" type="password" value={form.new_password} onChange={set("new_password")} required className="mt-1.5 rounded-xl" placeholder="At least 6 characters" />
            </div>
            <div>
              <Label className="text-xs uppercase tracking-[0.2em] text-stone-500">Confirm new password</Label>
              <Input data-testid="cp-confirm" type="password" value={form.confirm} onChange={set("confirm")} required className="mt-1.5 rounded-xl" placeholder="Re-enter new password" />
            </div>
            {error && <p data-testid="cp-error" className="text-sm text-red-600">{error}</p>}
            <Button data-testid="cp-submit" type="submit" disabled={busy} className="w-full rounded-full h-12 bg-emerald-900 hover:bg-emerald-800 text-white">
              {busy ? "Saving…" : "Update password"}
            </Button>
          </form>
        </motion.div>
      </div>
    </div>
  );
}
